import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  Smartphone, Monitor, LogOut, Trash2, Pencil, ShieldCheck, ShieldOff,
  History, KeyRound, RefreshCw,
} from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export const SecurityCenter = () => {
  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [history, setHistory] = useState([]);
  const [mfa, setMfa] = useState({ enabled: false, method: null });
  const [totpSetup, setTotpSetup] = useState(null); // {qr, secret}
  const [totpCode, setTotpCode] = useState('');
  const [mfaPassword, setMfaPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiCall('/api/security/sessions').then((r) => setSessions(r.sessions)).catch(() => {});
    apiCall('/api/security/trusted-devices').then((r) => setDevices(r.devices)).catch(() => {});
    apiCall('/api/security/login-history').then((r) => setHistory(r.events)).catch(() => {});
    apiCall('/api/security/sensitive-status').then((r) => setMfa({ enabled: r.mfa_enabled, method: r.mfa_method })).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const revokeSession = async (sid) => {
    try { await apiCall(`/api/security/sessions/${sid}`, { method: 'DELETE' }); toast.success('Session signed out'); load(); }
    catch (e) { toast.error(e.message); }
  };
  const revokeAll = async () => {
    try {
      const r = await apiCall('/api/security/sessions/revoke-all', { method: 'POST' });
      toast.success(`Signed out ${r.revoked} other session${r.revoked === 1 ? '' : 's'}`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const renameDevice = async (d) => {
    const name = window.prompt('Rename this device', d.name || '');
    if (!name?.trim()) return;
    try { await apiCall(`/api/security/trusted-devices/${d.id}`, { method: 'PATCH', body: JSON.stringify({ name }) }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const removeDevice = async (id) => {
    try { await apiCall(`/api/security/trusted-devices/${id}`, { method: 'DELETE' }); toast.success('Trusted device removed'); load(); }
    catch (e) { toast.error(e.message); }
  };
  const removeAllDevices = async () => {
    try { await apiCall('/api/security/trusted-devices', { method: 'DELETE' }); toast.success('All trusted devices removed'); load(); }
    catch (e) { toast.error(e.message); }
  };

  const startTotp = async () => {
    setBusy(true);
    try { const r = await apiCall('/api/security/mfa/totp/setup', { method: 'POST' }); setTotpSetup(r); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const enableTotp = async () => {
    setBusy(true);
    try {
      await apiCall('/api/security/mfa/totp/enable', { method: 'POST', body: JSON.stringify({ code: totpCode }) });
      toast.success('Authenticator app MFA enabled');
      setTotpSetup(null); setTotpCode(''); load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const enableEmailMfa = async () => {
    if (!mfaPassword) { toast.error('Enter your password to enable email MFA'); return; }
    setBusy(true);
    try {
      await apiCall('/api/security/mfa/email/enable', { method: 'POST', body: JSON.stringify({ password: mfaPassword }) });
      toast.success('Email OTP MFA enabled'); setMfaPassword(''); load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const disableMfa = async () => {
    if (!mfaPassword) { toast.error('Enter your password to disable MFA'); return; }
    setBusy(true);
    try {
      await apiCall('/api/security/mfa/disable', { method: 'POST', body: JSON.stringify({ password: mfaPassword }) });
      toast.success('MFA disabled'); setMfaPassword(''); load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const card = 'bg-white rounded-2xl border border-slate-200 p-5';

  return (
    <div className="space-y-6" data-testid="security-center">
      {/* MFA */}
      <div className={card} data-testid="security-mfa-section">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-1">
          {mfa.enabled ? <ShieldCheck className="w-5 h-5 text-emerald-500" /> : <ShieldOff className="w-5 h-5 text-slate-400" />}
          Multi-Factor Authentication
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          {mfa.enabled
            ? `Enabled via ${mfa.method === 'totp' ? 'authenticator app' : 'email codes'}. Required at login and for sensitive pages.`
            : 'Add a second factor to protect your account.'}
        </p>
        {!mfa.enabled && !totpSetup && (
          <div className="flex flex-wrap gap-3 items-center">
            <button onClick={startTotp} disabled={busy}
                    className="h-10 px-4 rounded-full bg-cyan-500 text-white text-sm font-semibold hover:bg-cyan-600 disabled:opacity-50"
                    data-testid="mfa-setup-totp-btn">
              Set up authenticator app
            </button>
            <div className="flex gap-2 items-center">
              <input type="password" value={mfaPassword} onChange={(e) => setMfaPassword(e.target.value)} placeholder="Password"
                     className="h-10 px-3 border border-slate-200 rounded-lg text-sm w-44" data-testid="mfa-password-input" />
              <button onClick={enableEmailMfa} disabled={busy}
                      className="h-10 px-4 rounded-full border border-cyan-300 text-cyan-600 text-sm font-semibold hover:bg-cyan-50 disabled:opacity-50"
                      data-testid="mfa-enable-email-btn">
                Enable email OTP MFA
              </button>
            </div>
          </div>
        )}
        {totpSetup && (
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img src={totpSetup.qr_png_base64} alt="TOTP QR code" className="w-40 h-40 border rounded-xl" data-testid="mfa-qr-image" />
            <div className="flex-1 space-y-2">
              <p className="text-sm text-slate-600">Scan with Google Authenticator (or similar), then enter the 6-digit code.</p>
              <p className="text-xs text-slate-400 break-all">Manual key: <code>{totpSetup.secret}</code></p>
              <div className="flex gap-2">
                <input value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       placeholder="123456" className="h-10 px-3 border border-slate-200 rounded-lg text-sm w-32 text-center tracking-widest"
                       data-testid="mfa-totp-code-input" />
                <button onClick={enableTotp} disabled={busy || totpCode.length < 6}
                        className="h-10 px-4 rounded-full bg-cyan-500 text-white text-sm font-semibold disabled:opacity-40"
                        data-testid="mfa-totp-confirm-btn">Confirm</button>
                <button onClick={() => setTotpSetup(null)} className="h-10 px-3 text-sm text-slate-500">Cancel</button>
              </div>
            </div>
          </div>
        )}
        {mfa.enabled && (
          <div className="flex gap-2 items-center">
            <input type="password" value={mfaPassword} onChange={(e) => setMfaPassword(e.target.value)} placeholder="Password"
                   className="h-10 px-3 border border-slate-200 rounded-lg text-sm w-44" data-testid="mfa-password-input" />
            <button onClick={disableMfa} disabled={busy}
                    className="h-10 px-4 rounded-full border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
                    data-testid="mfa-disable-btn">Disable MFA</button>
          </div>
        )}
      </div>

      {/* Active sessions */}
      <div className={card} data-testid="security-sessions-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Monitor className="w-5 h-5 text-cyan-600" /> Active Sessions</h3>
          <div className="flex gap-2">
            <button onClick={load} className="h-9 px-3 rounded-full text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={revokeAll} className="h-9 px-4 rounded-full border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50"
                    data-testid="revoke-all-sessions-btn">Sign out all other sessions</button>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {sessions.map((s) => (
            <div key={s.sid} className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  {s.browser} on {s.os}
                  {s.current && <span className="ml-2 text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">This device</span>}
                </p>
                <p className="text-xs text-slate-400">IP {s.ip} · signed in {fmt(s.created_at)} · last active {fmt(s.last_activity)}</p>
              </div>
              <button onClick={() => revokeSession(s.sid)}
                      className="h-8 px-3 rounded-full text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                      data-testid={`revoke-session-${s.sid}`}>
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          ))}
          {sessions.length === 0 && <p className="text-sm text-slate-400 py-3">No active sessions found.</p>}
        </div>
      </div>

      {/* Trusted devices */}
      <div className={card} data-testid="security-devices-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Smartphone className="w-5 h-5 text-cyan-600" /> Trusted Devices</h3>
          {devices.length > 0 && (
            <button onClick={removeAllDevices} className="h-9 px-4 rounded-full border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50"
                    data-testid="remove-all-devices-btn">Remove all</button>
          )}
        </div>
        <div className="divide-y divide-slate-100">
          {devices.map((d) => (
            <div key={d.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  {d.name}
                  {d.current && <span className="ml-2 text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">This device</span>}
                </p>
                <p className="text-xs text-slate-400">{d.browser} · {d.os} · IP {d.ip} · last used {fmt(d.last_seen)} · expires {fmt(d.expires_at)}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => renameDevice(d)} className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center"
                        data-testid={`rename-device-${d.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => removeDevice(d.id)} className="h-8 w-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center"
                        data-testid={`remove-device-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          {devices.length === 0 && <p className="text-sm text-slate-400 py-3">No trusted devices. You can trust a device during identity verification.</p>}
        </div>
      </div>

      {/* Login history */}
      <div className={card} data-testid="security-history-section">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3"><History className="w-5 h-5 text-cyan-600" /> Security Activity</h3>
        <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {history.map((h, idx) => (
            <div key={`${h.at}-${idx}`} className="py-2 flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2">
                <KeyRound className={`w-3.5 h-3.5 ${h.status === 'failure' ? 'text-red-500' : 'text-emerald-500'}`} />
                <span className="text-slate-700">{h.action.replace(/_/g, ' ')}</span>
                {h.status === 'failure' && <span className="text-xs text-red-500">failed</span>}
              </div>
              <span className="text-xs text-slate-400">{h.browser} · {h.ip} · {fmt(h.at)}</span>
            </div>
          ))}
          {history.length === 0 && <p className="text-sm text-slate-400 py-3">No recent security events.</p>}
        </div>
      </div>
    </div>
  );
};

export default SecurityCenter;
